import React, { useState, useEffect } from "react";
import { FaUser, FaLock, FaKey } from "react-icons/fa";
import { useStoreSnackbar } from "../../store/snackbar";
import { resetPasswordByOtp, changeLoginPassword } from "../../service/apiFetch";

export default function ForgetPassword({ changeScreen }) {
  const [username, setUsername] = useState("");
  const [page, setPage] = useState(1);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessagePassword, setErrorMessagePassword] = useState("");

  async function resetPasswordOtp() {
    if (validate()) {
      setLoading(true);
      const result = await resetPasswordByOtp({ username });
      if (result?.id) {
        setPage(2);
        setUserId(result.id);
        useStoreSnackbar.getState().showSnackbar({
          description: "OTP sent for password reset",
          title: "Sent OTP",
          color: "success",
        });
      } else {
        useStoreSnackbar.getState().showSnackbar({
          description: result?.response?.details?.message,
          title: result?.response?.details?.code,
          color: "error",
        });
      }
      setLoading(false);
    }
  }

  async function changePassword() {
    if (validatePasswod()) {
      if (otp?.length !== 4) {
        useStoreSnackbar.getState().showSnackbar({
          description: "OTP must be 4 digits",
          title: "Error",
          color: "error",
        });
        return;
      }
      if (newPassword !== confirmPassword) {
        useStoreSnackbar.getState().showSnackbar({
          description: "Passwords do not match",
          title: "Mismatch",
          color: "error",
        });
        return;
      }
      setLoading(true);
      const result = await changeLoginPassword({
        newPassword,
        id: userId,
        confirmPassword,
        otp,
      });
      if (result?.done) {
        setPage(3);
        useStoreSnackbar.getState().showSnackbar({
          description: "Password reset successfully",
          title: "Success",
          color: "success",
        });
      } else {
        useStoreSnackbar.getState().showSnackbar({
          description: result?.response?.details?.message,
          title: result?.response?.details?.code,
          color: "error",
        });
      }
      setLoading(false);
    }
  }

  const validate = () => {
    const errors = {};
    if (!username) errors.username = "Username is required";
    setErrorMessage(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswod = () => {
    const errors = {};
    if (!otp) errors.otp = "OTP is required";
    if (!newPassword) errors.newPassword = "New password is required";
    if (!confirmPassword) errors.confirmPassword = "Confirm password is required";
    setErrorMessagePassword(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    const t1 = setTimeout(() => setErrorMessage({}), 3000);
    const t2 = setTimeout(() => setErrorMessagePassword({}), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [errorMessage, errorMessagePassword]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[12rem]">
        <div className="border-gray-200 border-t-[#FFD338] w-[48px] h-[48px] animate-spin rounded-full border-4 border-solid"></div>
      </div>
    );
  }

  // Step 1: Enter username
  if (page === 1) {
    return (
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 mb-4">
          <FaKey className="text-[#FFD338]" />
          <h3 className="text-lg font-bold text-[#052c24]">Reset Password</h3>
        </div>

        <label className="block text-sm font-semibold text-[#052c24] mb-1">
          Reg. Mobile No. <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center border border-[#052c24] rounded-lg px-3 py-2 mb-2 bg-white focus-within:ring-2 focus-within:ring-[#FFD338] transition-all">
          <FaUser className="text-[#052c24] mr-2 text-sm" />
          <input
            className="w-full outline-none text-sm bg-transparent placeholder-gray-400"
            value={username}
            placeholder="Enter your mobile number"
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        {errorMessage?.username && (
          <p className="text-red-500 text-xs mb-2">{errorMessage.username}</p>
        )}

        <div className="flex justify-between mt-4 pb-3">
          <button
            className="bg-[#FFD338] hover:bg-[#d6aa08] text-[#052c24] font-semibold px-5 py-2 rounded-lg border border-black shadow-[0px_4px_10px_rgba(0,0,0,0.3)] transition-all text-sm"
            onClick={resetPasswordOtp}
          >
            Send OTP
          </button>
          <button
            className="bg-white hover:bg-gray-100 text-[#052c24] font-semibold px-5 py-2 rounded-lg border border-gray-300 transition-all text-sm"
            onClick={() => changeScreen("LOGIN")}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Enter OTP + new password
  if (page === 2) {
    return (
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 mb-4">
          <FaLock className="text-[#FFD338]" />
          <h3 className="text-lg font-bold text-[#052c24]">Set New Password</h3>
        </div>

        <label className="block text-sm font-semibold text-[#052c24] mb-1">
          Enter OTP <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center border border-[#052c24] rounded-lg px-3 py-2 mb-2 bg-white focus-within:ring-2 focus-within:ring-[#FFD338] transition-all">
          <FaKey className="text-[#052c24] mr-2 text-sm" />
          <input
            className="w-full outline-none text-sm bg-transparent placeholder-gray-400"
            type="number"
            value={otp}
            placeholder="Enter 4-digit OTP"
            onChange={(e) => setOtp(e.target.value)}
          />
        </div>
        {errorMessagePassword?.otp && (
          <p className="text-red-500 text-xs mb-2">{errorMessagePassword.otp}</p>
        )}

        <label className="block text-sm font-semibold text-[#052c24] mb-1">
          New Password <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center border border-[#052c24] rounded-lg px-3 py-2 mb-2 bg-white focus-within:ring-2 focus-within:ring-[#FFD338] transition-all">
          <FaLock className="text-[#052c24] mr-2 text-sm" />
          <input
            className="w-full outline-none text-sm bg-transparent placeholder-gray-400"
            type="password"
            value={newPassword}
            placeholder="Enter new password"
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>
        {errorMessagePassword?.newPassword && (
          <p className="text-red-500 text-xs mb-2">{errorMessagePassword.newPassword}</p>
        )}

        <label className="block text-sm font-semibold text-[#052c24] mb-1">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center border border-[#052c24] rounded-lg px-3 py-2 mb-2 bg-white focus-within:ring-2 focus-within:ring-[#FFD338] transition-all">
          <FaLock className="text-[#052c24] mr-2 text-sm" />
          <input
            className="w-full outline-none text-sm bg-transparent placeholder-gray-400"
            type="password"
            value={confirmPassword}
            placeholder="Confirm new password"
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        {errorMessagePassword?.confirmPassword && (
          <p className="text-red-500 text-xs mb-2">{errorMessagePassword.confirmPassword}</p>
        )}

        <div className="mt-4 pb-3">
          <button
            className="w-full bg-[#FFD338] hover:bg-[#d6aa08] text-[#052c24] font-semibold px-5 py-2 rounded-lg border border-black shadow-[0px_4px_10px_rgba(0,0,0,0.3)] transition-all text-sm"
            onClick={changePassword}
          >
            Reset Password
          </button>
        </div>
      </div>
    );
  }

  // Step 3: Success
  return (
    <div className="text-center py-8 px-5">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="font-bold text-lg text-[#052c24]">Password Reset Successfully!</p>
      <button
        className="mt-4 bg-[#FFD338] hover:bg-[#d6aa08] text-[#052c24] font-semibold px-6 py-2 rounded-lg border border-black shadow-[0px_4px_10px_rgba(0,0,0,0.3)] transition-all text-sm"
        onClick={() => changeScreen("LOGIN")}
      >
        Back to Login
      </button>
    </div>
  );
}
